import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import courseProgress from '../../utils/courseProgress';
import '../../styles/EstudoCurso.css';

const EstudoCurso = () => {
  const { id } = useParams();
  const [curso, setCurso] = useState(null);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [, setCompletedModules] = useState([]);
  const [hasProva, setHasProva] = useState(false);
  const isAuthenticated = Boolean(localStorage.getItem('token'));

  // Carregar dados do curso e progresso
  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        
        const [courseResponse, modulesResponse] = await Promise.all([
          apiService.cursos.get(id),
          isAuthenticated ? apiService.modulos.listByCourse(id) : Promise.resolve(null),
        ]);
        setCurso(courseResponse.data);
        setModulos(modulesResponse?.data || courseResponse.data.modulos || []);

        let progress = courseProgress.getCompletedModules(id);

        if (isAuthenticated) {
          try {
            const [provaResponse, progressResponse] = await Promise.all([
              apiService.cursos.getProva(id),
              apiService.progress.getCourse(id),
            ]);
            setHasProva(Boolean(provaResponse.data.questions?.length));
            progress = [...new Set([
              ...progress,
              ...(progressResponse.data.completed_module_ids || []),
            ].map(Number))];
            courseProgress.saveProgress(id, progress);
          } catch {
            setHasProva(false);
          }
        }

        setCompletedModules(progress);

      } catch (err) {

        setError('Não foi possível carregar os dados do curso. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCourseData();
    }
  }, [id, isAuthenticated]);

  // Listener para atualizações de progresso de outros componentes
  useEffect(() => {
    const handleProgressUpdate = (event) => {
      if (event.detail.courseId === id) {
        setCompletedModules(event.detail.completedModules);
      }
    };

    window.addEventListener('courseProgressUpdated', handleProgressUpdate);
    
    return () => {
      window.removeEventListener('courseProgressUpdated', handleProgressUpdate);
    };
  }, [id]);

  const calculateProgress = () => {
    return courseProgress.calculateProgress(id, modulos.length);
  };

  const isModuleCompleted = (moduleId) => {
    return courseProgress.isModuleCompleted(id, moduleId);
  };

  // Mostrar loading apenas para dados do curso
  if (loading) {
    return (
      <div className="estudo-curso-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando conteúdo do curso...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="estudo-curso-container">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Ops! Algo deu errado</h2>
          <p>Não conseguimos carregar o conteúdo do curso. Tente novamente.</p>
          <button onClick={() => window.location.reload()} className="retry-button">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  if (!curso) {
    return (
      <div className="estudo-curso-container">
        <div className="error-container">
          <div className="error-icon">📚</div>
          <h2>Curso não encontrado</h2>
          <p>O curso que você está procurando não existe ou foi removido.</p>
        <Link to={isAuthenticated ? '/meus-cursos' : '/cursos'} className="back-link">Voltar aos Cursos</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="estudo-curso-container" data-page="estudo-curso" data-component="course-study">
      {/* Breadcrumb Navigation */}
      <nav className="estudo-breadcrumb-nav">
        <Link to={isAuthenticated ? '/meus-cursos' : `/curso/${id}`} className="estudo-breadcrumb-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 7L12 2L3 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          {isAuthenticated ? 'Meus Cursos' : 'Detalhes do curso'}
        </Link>
        <span className="estudo-breadcrumb-sep">›</span>
        <span className="estudo-breadcrumb-current">{curso.title}</span>
      </nav>

      {/* Action Buttons */}
      <div className={`estudo-action-buttons ${isAuthenticated && hasProva ? 'with-exam-callout' : ''}`}>
        <Link to={isAuthenticated ? '/meus-cursos' : `/curso/${id}`} className="estudo-btn estudo-btn-secondary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Voltar
        </Link>
      </div>

      {isAuthenticated && hasProva && (
        <section className="estudo-exam-callout" aria-labelledby="estudo-exam-callout-title">
          <div className="estudo-exam-callout-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
              <rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M9 12H15M9 16H13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
            <span>✓</span>
          </div>
          <div className="estudo-exam-callout-copy">
            <h2 id="estudo-exam-callout-title">Você se sente preparado para a prova?</h2>
            <p>Você pode fazer a prova sempre que quiser</p>
          </div>
          <Link to={`/curso/${id}/prova`} className="estudo-exam-callout-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3.89543 5 5 3.89543 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Fazer a prova
          </Link>
        </section>
      )}

      {/* Modules Section */}
      <section className="estudo-modules-section">
        <div className="estudo-section-header">
          <h2>
            Módulos do curso
            <span className="estudo-modules-course-title">{curso.title}</span>
          </h2>
          <p>Clique em qualquer módulo para começar a estudar</p>
        </div>

        {modulos.length === 0 ? (
          <div className="estudo-empty-state">
            <div className="estudo-empty-icon">📖</div>
            <h3>Nenhum módulo disponível</h3>
            <p>Este curso ainda não possui módulos de estudo.</p>
          </div>
        ) : (
          <div className="estudo-modules-grid">
            {modulos.map((modulo, index) => (
              <Link 
                key={modulo.id}
                to={`/curso/${curso.id}/modulo/${modulo.id}`}
                className={`estudo-module-card ${isModuleCompleted(modulo.id) ? 'completed' : ''}`}
                data-status={isModuleCompleted(modulo.id) ? 'completed' : 'pending'}
              >
                <div className="estudo-module-number" aria-hidden="true">
                  <span>{index + 1}</span>
                </div>

                <div className="estudo-module-content">
                  <h3 className="estudo-module-title">{modulo.title}</h3>
                  <p className="estudo-module-description">
                    {modulo.description || 'Clique para acessar o conteúdo deste módulo'}
                  </p>
                  <span className="estudo-module-duration">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
                      <path d="M12 7V12L15.5 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {modulo.duration || '~15 min'}
                  </span>
                </div>

                <div className="estudo-module-status">
                  {isModuleCompleted(modulo.id) ? (
                    <span className="estudo-status-badge completed">
                      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="10" cy="10" r="9" fill="currentColor"/>
                        <path d="M6 10.2L8.5 12.6L14 7.4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Concluído
                    </span>
                  ) : (
                    <span className="estudo-status-badge pending">
                      <svg width="17" height="17" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.7"/>
                        <path d="M10 5.8V10L12.8 11.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Pendente
                    </span>
                  )}
                </div>

                <div className="estudo-module-arrow" aria-hidden="true">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Quick Stats */}
      <aside className="estudo-quick-stats">
        <div className="estudo-stat-card">
          <div className="estudo-stat-icon">📚</div>
          <div className="estudo-stat-info">
            <span className="estudo-stat-number">{modulos.length}</span>
            <span className="estudo-stat-label">Módulos</span>
          </div>
        </div>
        
        <div className="estudo-stat-card">
          <div className="estudo-stat-icon">⏱️</div>
          <div className="estudo-stat-info">
            <span className="estudo-stat-number">{curso.hours || 'Não informada'}</span>
            <span className="estudo-stat-label">Carga horária</span>
          </div>
        </div>
        
        <div className="estudo-stat-card">
          <div className="estudo-stat-icon">🎯</div>
          <div className="estudo-stat-info">
            <span className="estudo-stat-number">{calculateProgress()}%</span>
            <span className="estudo-stat-label">Concluído</span>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default EstudoCurso;
